import React, {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {buildOfferContext} from 'middleware/analytics/contexts/offerContext'
import {
  getAlternativeOffersList,
  getOffersEntityWithoutVioOffers
} from 'modules/alternativeOffers/selectors'
import {
  getAnonymousId,
  getDeviceLayout,
  getLanguageCode
} from 'modules/meta/selectors'
import {getShowTotalPrices} from 'modules/sapiSearch/selectors'
import {getIsOTAMode} from 'modules/search/selectors'

import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity
} from '@daedalus/core/src/analytics/types/Events'
import {SearchOffer} from '@daedalus/core/src/offer/types/SearchOffer'
import {AlternativeOffersContext} from '@daedalus/shared/src/booking/AlternativeOffers/AlternativeOffersContext'
import {AlternativeOffersList} from '@daedalus/shared/src/booking/AlternativeOffers/AlternativeOffersList'

/**
 * Builds the offer context for the alternative offers list.
 * ! Contained logic to be used only in the context of the alternative offers list.
 * @returns offerContextBuilder
 */
const useGetAlternativeOfferContextBuilder = () => {
  const offersEntityforTracking = useSelector(getOffersEntityWithoutVioOffers)

  const deviceLayout = useSelector(getDeviceLayout)
  const isOTAMode = useSelector(getIsOTAMode)

  const offerContextBuilder = useCallback(
    (offer: SearchOffer) => {
      return buildOfferContext(
        offer.id,
        'APAlternativeOffersOverlay',
        offersEntityforTracking,
        offer,
        1,
        false,
        isOTAMode,
        null, //we filter out vio offers so there's no locked/unlocked offers
        null, //we don't have split booking details in the alternative offers list
        deviceLayout
      )
    },
    [offersEntityforTracking, isOTAMode, deviceLayout]
  )

  return {
    offerContextBuilder
  }
}

export const AlternativeOffersListContainer = () => {
  const offers = useSelector(getAlternativeOffersList)

  const dispatch = useDispatch()
  const anonymousId = useSelector(getAnonymousId)

  const showTotalPrices = useSelector(getShowTotalPrices) //check if its the right place
  const lang = useSelector(getLanguageCode)

  const {offerContextBuilder} = useGetAlternativeOfferContextBuilder()

  const onOfferClick = (offer: SearchOffer) => {
    dispatch(
      trackEvent({
        action: Action.Clicked,
        category: Category.User,
        entity: Entity.Offer,
        analyticsContext: {
          offerContext: offerContextBuilder(offer)
        }
      })
    )
  }

  const onOfferViewed = (offer: SearchOffer) => {
    dispatch(
      trackEvent({
        action: Action.Viewed,
        entity: Entity.Offer,
        category: Category.User,
        analyticsContext: {
          offerContext: offerContextBuilder(offer)
        }
      })
    )
  }

  return (
    <AlternativeOffersContext.Provider
      value={{
        showTotalPrices,
        onOfferClick,
        lang,
        anonymousId,
        onOfferViewed
      }}
    >
      <AlternativeOffersList offers={offers} />
    </AlternativeOffersContext.Provider>
  )
}
